import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authAPI } from "../../../services/apiService";
import { reconcileSession, setSessionCookie } from "../../../utils/sessionCookie";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { Showcase } from "../../components/auth/Showcase";
import { FormError, PasswordInput } from "../../components/auth/fields";
import { AUTH_PATHS } from "../../components/auth/signupState";
import { Field, TextInput, isEmail } from "../../components/crm/leadsKit";
import { PrimaryButton } from "../../components/ui";

/**
 * Sign in (design: screens/SignIn.tsx), on the same API and session handling as
 * the old pages/Login.js: POST /api/auth/login, then token + user in
 * localStorage, the olli_session cookie, and the `auth:login` event that
 * FacilityContext listens for. The design then plays the Welcome splash,
 * which opens the dashboard.
 */

const WRONG = "Email or password is incorrect. Check them and try again.";

export default function SignIn() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [loading, setLoading] = useState(false);

  // Same self-heal as the old login page: drop a stale "logged in" cookie when there is no token.
  useEffect(() => {
    reconcileSession();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!email.trim()) next.email = "Enter your work email";
    else if (!isEmail(email)) next.email = "Enter an email like name@clinic.in";
    if (!password) next.password = "Enter your password";
    setErrors(next);
    setFormError("");
    if (Object.keys(next).length) return;
    setLoading(true);
    try {
      const response = await authAPI.login({ email, password });
      if (response.data.success) {
        localStorage.setItem("token", response.data.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.data.user));
        setSessionCookie();
        window.dispatchEvent(new Event("auth:login"));
        navigate(AUTH_PATHS.welcome, { replace: true });
        return;
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      // 401 is a wrong email or password; other messages (unverified, deactivated) come from the server as is.
      setFormError(err.response?.status === 401 ? WRONG : err.response?.data?.message || "Login failed. Please try again.");
    }
  };

  return (
    <AuthLayout
      topRight={
        <p className="text-small text-muted">
          New to Olli?{" "}
          <Link to={`${AUTH_PATHS.signup}/basic`} className="font-semibold text-ink underline-offset-4 hover:underline">
            Create account
          </Link>
        </p>
      }
      aside={<Showcase step="signin" />}
    >
      <form onSubmit={submit} noValidate className="flex animate-fade-in flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-h1 font-semibold">Sign in</h1>
          <p className="text-body text-muted">Welcome back to Olli AI</p>
        </div>

        {formError && <FormError>{formError}</FormError>}

        <div className="flex flex-col gap-5">
          <Field id="email" label="Work email" error={errors.email}>
            <TextInput
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              invalid={!!errors.email}
            />
          </Field>
          <div className="flex flex-col gap-2">
            <Field id="password" label="Password" error={errors.password}>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                invalid={!!errors.password}
              />
            </Field>
            <Link to={AUTH_PATHS.forgot} className="self-end text-small font-medium text-sky-deep hover:underline">
              Forgot password?
            </Link>
          </div>
        </div>

        <PrimaryButton type="submit" loading={loading} loadingLabel="Signing in…">
          Sign in
        </PrimaryButton>
      </form>
    </AuthLayout>
  );
}
